import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getBusiness, getBusinessReviews } from "../utils/publicApi";
import { toAbsoluteUrl } from "../utils/url";
import "./BusinessPublic.scss";

type PubReview = {
  rating: number;
  comment: string;
  displayName: string;
  reply?: string;
  createdAt: string;
};

const PublicReviewCard: React.FC<{ r: PubReview }> = ({ r }) => (
  <div className="reviewCard">
    <div className="top">
      <div className="stars">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
    </div>
    <div className="body">{r.comment}</div>
    <div className="author">— {r.displayName}</div>
    {r.reply && (
      <div className="reply">
        <div className="label">Réponse du commerçant</div>
        <div>{r.reply}</div>
      </div>
    )}
  </div>
);

const BusinessPublic: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<any>(null);
  const [reviews, setReviews] = useState<PubReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [sort, setSort] = useState<"recent" | "top">("recent");
  const [rating, setRating] = useState<"all" | 1 | 2 | 3 | 4 | 5>("all");

  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await getBusiness(slug);
        if (signal.aborted) return;
        setData(d);

        const ratingParam = rating === "all" ? "all" : (String(rating) as "1" | "2" | "3" | "4" | "5");
        const r = await getBusinessReviews(slug, { sort, rating: ratingParam, page: 1, limit: 10 });
        if (signal.aborted) return;
        setReviews((r.reviews || []) as PubReview[]);
      } catch (e: any) {
        if (!signal.aborted) setErr(e?.message || "Erreur de chargement.");
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [slug, sort, rating]);

  if (err) return <div className="businessPublic"><div className="error">{err}</div></div>;
  if (!data) return <div className="businessPublic"><div className="loading">Chargement…</div></div>;

  const dist = data.stats?.distribution || {};
  const total = Math.max(1, data.stats?.totalReviews || 0);
  const websiteUrl = toAbsoluteUrl(data.business.website);

  // Build the internal public profile path (prefer the business-provided slug, else route slug, else id)
  const profileKey: string | undefined =
    data.business.slug || slug || data.business.id;
 const profilePath = profileKey ? `/p/${encodeURIComponent(profileKey)}` : "";

  return (
    <div className="businessPublic">
      <div className="header">
        <div>
          <h1>{data.business.name}</h1>
          {data.business.tagline && <div className="tagline">{data.business.tagline}</div>}
          <div className="meta">{data.business.addressCity}</div>
        </div>
        <div className="grade">
          <div className="avg">★ {Number(data.stats.avgRating || 0).toFixed(1)}</div>
          <div className="count">{data.stats.totalReviews} avis</div>
        </div>
      </div>

      <div className="dist">
        {[5, 4, 3, 2, 1].map((s) => (
          <div key={s} className="bar">
            <span>{s}★</span>
            <div className="track">
              <div className="fill" style={{ width: `${((dist[s] || 0) / total) * 100}%` }} />
            </div>
            <em>{dist[s] || 0}</em>
          </div>
        ))}
      </div>

      <div className="actions">
        {/* Public profile (your internal page) */}
        {profilePath && (
          <Link className="primary" to={profilePath}>
            Profil
          </Link>
        )}

        {/* Leave a review (internal route) */}
        <Link className="secondary" to={`/leave-review/${data.business.id}`}>
          Laisser un avis
        </Link>

        {/* External company website */}
        {websiteUrl && (
          <a className="secondary" href={websiteUrl} target="_blank" rel="noreferrer">
            Site
          </a>
        )}
      </div>

      <div className="filters">
        <select value={sort} onChange={(e) => setSort(e.target.value as "recent" | "top")}>
          <option value="recent">Plus récents</option>
          <option value="top">Mieux notés</option>
        </select>
        <select
          value={String(rating)}
          onChange={(e) => setRating((e.target.value === "all" ? "all" : Number(e.target.value)) as any)}
        >
          <option value="all">Toutes les notes</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>{n} ★</option>
          ))}
        </select>
      </div>

      <div className="reviewList">
        {loading ? (
          <div className="loading">Chargement des avis…</div>
        ) : reviews.length === 0 ? (
          <div className="empty">Aucun avis.</div>
        ) : (
          reviews.map((r, i) => <PublicReviewCard key={i} r={r} />)
        )}
      </div>

      <div className="disclaimer">Avis collectés via ReviewBoost AI — non affilié à Google/Yelp.</div>
    </div>
  );
};

export default BusinessPublic;
